import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_MAIL_RECIPIENT_GROUPS } from '@/api/endpoints';
import { MAIL_RECIPIENT_GROUP_MEMBERS_QUERY_KEY } from '@/api/queryKeys';
import type { RecipientGroupMemberResponse } from '@/types/mail';
import { unwrapAxiosResponse } from '@/utils/axios';

interface Params {
  groupId?: number;
  studyYearId?: number;
}

const dropDuplicateMembers = (
  members: RecipientGroupMemberResponse[],
): RecipientGroupMemberResponse[] => {
  const seenIds = new Set<number>();

  return members.filter(({ id }) => {
    if (seenIds.has(id)) {
      return false;
    }

    seenIds.add(id);

    return true;
  });
};

export const useRecipientGroupMembers = ({ groupId, studyYearId }: Params) => {
  const { data: members, isFetching } = useQuery<
    RecipientGroupMemberResponse[]
  >({
    queryKey: [...MAIL_RECIPIENT_GROUP_MEMBERS_QUERY_KEY, studyYearId, groupId],
    queryFn: () =>
      apiClient
        .get<
          RecipientGroupMemberResponse[]
        >(`${API_MAIL_RECIPIENT_GROUPS}/${groupId}/members`, { params: { studyYearId } })
        .then(unwrapAxiosResponse)
        .then(dropDuplicateMembers),
    enabled: !!groupId && !!studyYearId,
    staleTime: Infinity,
  });

  return { members: members ?? [], isFetching };
};
